<template>
  <div class="bg-grey-975 overflow-hidden flex flex-col z-20">
    <div class="hidden lg:flex items-center px-4 pt-1 pb-1 xl:pt-2 xl:pb-[17px] z-10">
      <h2 class="text-h5 xl:text-h4 font-bold">Flux</h2>
      <div class="ml-auto">
        <PForm
          :submit-fn="saveFlux"
          @reset="resetForm"
          title="Nouveau flux"
          v-model:updating="isSaveFluxModalOpened"
          @close="isSaveFluxModalOpened = false"
        >
          <PInput
            data-sentry-id="NewSavedFluxNameInput"
            label="Nom"
            ref="inputRef"
            v-model="fluxName"
            :error-messages="fluxNameErrors"
          />

          <template #activator="{}"><span class="hidden"></span></template>
        </PForm>

        <KebabMenu placement="left-start" aria-label="Action rapide sur le flux">
          <template #default="{ close }">
            <button
              data-sentry-id="OpenNewSavedFluxModal"
              :disabled="!canSaveFlux"
              class="text-left"
              :class="{ 'text-grey-425 cursor-not-allowed': !canSaveFlux }"
              @click="isSaveFluxModalOpened = true"
            >
              Enregistrer le flux
            </button>
            <InvokeSavedFlux
              v-if="!readonly"
              :saved-flux-items="savedFluxItems"
              @apply="applySavedFlux($event, close)"
            />
            <button
              data-sentry-id="ClearFluxBtn"
              v-if="!readonly"
              @click="isEmptyStreamModalOpen = true"
              class="border-t pt-1v border-grey-925 flex text-error-425"
            >
              Vider le flux
            </button>
          </template>
        </KebabMenu>
      </div>
    </div>
    <div class="overflow-y-auto grow shrink px-2 lg:px-4 py-2 lg:h-0 relative">
      <div class="flex flex-col gap-1.5" v-if="!isDraft">
        <div v-for="(upstream, index) in upstreamElements" :key="upstream.id">
          <UpstreamElement
            :lfId="letterFile!.id"
            :element="upstream"
            :init="index === 0"
            :is-current="letterFile!.status === LetterFileStatus.CURRENT"
            @add-to-end-stream="addToEndStream(upstream)"
          />
        </div>
      </div>
      <CurrentElement
        v-if="current && letterFile?.downstream"
        :letter-file="letterFile!"
        :element="current!"
        ref="currentElement"
        @add-to-end-stream="addToEndStream(current!)"
      />
      <DraggableList
        group-name="downstreamElement"
        data-id-attr="data-downstream-id"
        @choose="draggedElementId = $event.oldIndex!"
        @un-choose="draggedElementId = null"
        @drop="reorderDownstream"
        handle="svg.drag-handle"
        class="flex flex-col gap-1.5"
      >
        <div v-for="(downstream, i) in downstreamElements" :key="downstream.id" :data-downstream-id="downstream.id">
          <DownstreamElement
            :element="downstream"
            :is-last="i >= downstreamElements.length - 1"
            :is-current="letterFile!.status === LetterFileStatus.CURRENT"
            :writable="letterFile?.status === LetterFileStatus.CURRENT || isDraft"
            @delete="openDeleteFromStream(downstream)"
            :class="{
              'border-dashed': draggedElementId === i,
              'last-downstream-element': i >= downstreamElements.length - 1,
            }"
            @up="move(downstream, 'up')"
            @down="move(downstream, 'down')"
            @add-to-end-stream="addToEndStream(downstream)"
          />
        </div>
      </DraggableList>

      <div v-if="loading" class="h-full flex flex-col animate-pulse gap-3">
        <div v-for="i in 2" :key="i" class="bg-white px-2 py-1 rounded-lg w-full">
          <div class="bg-grey-900 h-2 w-6 mb-1"></div>
          <div class="flex justify-between">
            <div class="bg-grey-900 h-3 w-2/3"></div>
            <div class="bg-grey-900 h-3 w-12"></div>
          </div>
        </div>

        <div class="px-4 py-4 border-2 rounded-lg bg-blue-france-975 w-full">
          <div class="bg-grey-900 h-3 w-full"></div>
        </div>

        <div class="px-2 py-1 border border-blue-france-sun-113 rounded-md bg-blue-france-975 w-full">
          <div class="bg-grey-900 h-3 w-full"></div>
        </div>
      </div>

      <div class="px-2 py-1 bg-grey-925 rounded-md mt-1.5">
        <span class="font-bold">Archivé</span>
      </div>
    </div>
    <div class="lg:hidden" :style="{ 'padding-bottom': `calc(${fluxActionsHeight + 100}px + 2rem)` }"></div>
    <FluxActions
      ref="fluxActions"
      class="fixed bottom-0 left-0 w-full lg:static"
      :next="nextElementLabel"
      @undraft="$emit('undraft')"
      @new-downstream="onNewDownstream"
    />
    <DeleteFromStreamModal
      v-if="currentStreamToDelete"
      v-model="isDeleteFromStreamModalOpen"
      :stream-element="currentStreamToDelete"
      :is-in-cc="isInCc"
      @delete-from-stream="removeDownstream"
    ></DeleteFromStreamModal>
    <EmptyStreamModal v-model="isEmptyStreamModalOpen" @empty-stream="emptyStream"></EmptyStreamModal>
  </div>
</template>

<script setup lang="ts">
import { FormOperation } from "@/app/types";
import { PForm, PInput, usePastaValidation } from "@pasta/box-v2";
import { required } from "@vuelidate/validators";
import { useElementSize } from "@vueuse/core";
import { clone, findIndex, pick, uniqBy } from "lodash";
import { type SortableEvent } from "sortablejs";
import { computed, inject, nextTick, onMounted, ref, watch } from "vue";
import DraggableList from "../../../components/DraggableList.vue";
import KebabMenu from "../../../components/KebabMenu.vue";
import { useNotifyAsyncOperation } from "../../../composable/useAsyncOperation";
import { type FluxElementDto } from "../../../dto/flux-element.dto";
import { LetterFileStatus } from "../../../dto/letter-file.dto";
import { type SavedFluxDto } from "../../../dto/saved-flux.dto";
import { useLetterFileCCUserStore } from "../../../stores/cc-user.store";
import { useLetterFileSavedFluxStore } from "../../../stores/letter-file-saved-flux.store";
import { useLetterFileStore } from "../../../stores/letter-file.store";
import { type FormProvide, FormToken } from "../utils";
import CurrentElement from "./CurrentElement.vue";
import DeleteFromStreamModal from "./DeleteFromStreamModal.vue";
import DownstreamElement from "./DownstreamElement.vue";
import EmptyStreamModal from "./EmptyStreamModal.vue";
import FluxActions from "./FluxActions.vue";
import InvokeSavedFlux from "./InvokeSavedFlux.vue";
import UpstreamElement from "./UpstreamElement.vue";

interface Props {
  readonly: boolean;
  loading: boolean;
}

defineProps<Props>();
defineEmits<{ (e: "undraft"): void }>();

const letterFileStore = useLetterFileStore();
const letterFileSavedFluxStore = useLetterFileSavedFluxStore();
const ccUserStore = useLetterFileCCUserStore();
const notifyAsyncOperation = useNotifyAsyncOperation();
const { refresh, letterFile } = inject<FormProvide>(FormToken)!;

const isDraft = computed(() => letterFile.value?.draft ?? false);
const draggedElementId = ref<number | null>(null);
const isSaveFluxModalOpened = ref(false);
const isDeleteFromStreamModalOpen = ref(false);
const isEmptyStreamModalOpen = ref(false);
const currentStreamToDelete = ref();
const inputRef = ref<any>(null);

watch(
  () => isSaveFluxModalOpened.value,
  async () => {
    if (isSaveFluxModalOpened.value) {
      await nextTick();
      inputRef.value?.mainElement?.focus();
    }
  }
);

const downstreamElements = computed(() => {
  return letterFile.value?.downstream || [];
});
const upstreamElements = computed(() => letterFile.value?.upstream || []);
const current = computed(() => {
  return letterFile.value?.current;
});
const nextElementLabel = computed(() => {
  return downstreamElements.value.find(el => el.position === 1)?.name || "";
});

function openDeleteFromStream(element: FluxElementDto) {
  isDeleteFromStreamModalOpen.value = true;
  currentStreamToDelete.value = element;
}

async function removeDownstream(element: FluxElementDto, addToCC = true) {
  await notifyAsyncOperation(async () => {
    await letterFileStore.removeDownstream(letterFile.value!.id, element.id);
    if (addToCC) {
      await ccUserStore.create({
        mail: element.mail,
        letterFile: letterFile.value!.id,
      });
    }
    isDeleteFromStreamModalOpen.value = false;
    currentStreamToDelete.value = undefined;
    await refresh();
  }, FormOperation.FLUX);
}

function isInCc(element: FluxElementDto): boolean {
  return letterFile.value?.ccUsers?.some(ccUser => ccUser.mail === element.mail) || false;
}

async function addToEndStream(element: FluxElementDto) {
  await notifyAsyncOperation(async () => {
    await letterFileStore.appendDownstreamWithPosition(
      letterFile.value!.id,
      element.mail,
      downstreamElements.value.length + 1
    );
    await refresh();
  }, FormOperation.FLUX);
}

async function reorderDownstream(_event: SortableEvent, ids: number[]) {
  await notifyAsyncOperation(async () => {
    await letterFileStore.reorderDownstream(letterFile.value!.id, [letterFile.value!.current!.id, ...ids]);
    await refresh();
  }, FormOperation.FLUX);
}

async function move(element: FluxElementDto, direction: "up" | "down") {
  const downstream = clone(letterFile.value!.downstream!);
  const fromIndex = findIndex(downstream, element);
  const toIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;
  const el = downstream!.splice(fromIndex, 1)[0];
  downstream!.splice(toIndex, 0, el);
  await reorderDownstream(
    {} as SortableEvent,
    downstream.map(el => el.id)
  );
}

const currentElement = ref<any>(null);
onMounted(() => {
  if (currentElement.value) {
    (currentElement.value.$el as HTMLElement).scrollIntoView({
      behavior: "smooth",
    });
  }
});

// ====== Saved flux =======
const canSaveFlux = computed(() => (letterFile.value?.downstream || []).length >= 1);

async function emptyStream(addToCC: boolean) {
  await notifyAsyncOperation(async () => {
    isEmptyStreamModalOpen.value = false;
    if (addToCC) {
      for (const element of uniqBy(downstreamElements.value, "mail")) {
        if (isInCc(element)) continue;
        await ccUserStore.create({
          mail: element.mail,
          letterFile: letterFile.value!.id,
        });
      }
    }
    await letterFileStore.emptyDownstream(letterFile.value!.id);
    await refresh();
  }, FormOperation.FLUX);

  close();
}

const validation = usePastaValidation({ $stopPropagation: true });
const [fluxName, fluxNameErrors] = validation.ref("", [
  { validator: required, message: "Veuillez saisir un nom pour le flux" },
]);

async function saveFlux() {
  const isValid = await validation.validate();
  if (!isValid) return false;
  await notifyAsyncOperation(async () => {
    await letterFileSavedFluxStore.create({
      name: fluxName.value,
      flux: (letterFile.value!.downstream || []).map(element => pick(element, "mail", "name")),
    });
    await fetchSavedFlux();
  }, FormOperation.FLUX);
  return true;
}

function resetForm() {
  fluxName.value = "";
  validation.reset();
}

const savedFluxItems = computed<SavedFluxDto[]>(() => letterFileSavedFluxStore.list.data);

async function fetchSavedFlux() {
  console.log("fetchsavedflux");
  await letterFileSavedFluxStore.getMines();
}

async function applySavedFlux(id: number, close: () => void) {
  await notifyAsyncOperation(async () => {
    await letterFileStore.applyFlux(letterFile.value!.id, id);
    await refresh();
  }, FormOperation.FLUX);
  close();
}

async function onNewDownstream() {
  await nextTick();
  (document.querySelector(".last-downstream-element .delete-btn") as HTMLButtonElement).focus();
}

const fluxActions = ref<any>();
const fluxActionsSize = useElementSize(fluxActions);
const fluxActionsHeight = ref(0);
watch(
  () => fluxActionsSize.height.value,
  value => {
    requestAnimationFrame(() => {
      fluxActionsHeight.value = value || 0;
    });
  }
);
</script>
